// Media Library — page container (WL-023). Wires the app's existing media +
// storage reads to the pure MediaLibraryView, and owns page UI state (type tab,
// search, sort, view mode, pagination, selection) + the Delete modal.
//
// Server state uses react-query (the app's read layer) — useQuery for the two
// reads, keyed by resource; mutations (delete) invalidate the media key rather
// than hand-refetching. No hand-rolled useEffect fetch race.
//
// EW-1282: Move-to-Folder and New-Folder are NOT wired here (MoveMediaModal
// stays available at ../../components/MediaLibrary/MoveMediaModal for when a
// backend media-move/media-folder endpoint exists — none does today; the only
// folders API, /api/v2/folders/, organizes Wheels, not media files). Their
// entry points are disabled in MediaLibraryView with a "coming soon" tooltip
// instead of silently no-op'ing, so re-enabling later is: (1) build the BE
// endpoint, (2) re-add the mutation + modal wiring removed here, (3) drop the
// `disabled` in MediaLibraryView.jsx.
import React, { useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import GalleryHeader from "../../components/WheelGallery/Header/GalleryHeader";
import MediaLibraryView from "../../components/MediaLibrary/MediaLibraryView";
import DeleteMediaModal from "../../components/MediaLibrary/DeleteMediaModal";
import DeleteMediaBlockedModal from "../../components/MediaLibrary/DeleteMediaBlockedModal";
import MediaPreviewModal from "../../components/MediaLibrary/MediaPreviewModal";
import RenamePaletteDialog from "../../components/SegmentBuilder/Shared/ColorPalette/RenamePaletteDialog";
import { getTotalMedia, getTotalStorage, deleteMediaFiles, renameMedia, getMediaWheels, removeMediaFromWheels } from "../../services/api";
import { normalizeMediaResponse, normalizeMediaTypes, normalizeStorage } from "./mediaAdapter";
import { filterMedia, sortMedia, paginate, COLUMN_SORT_KEYS } from "./helpers";
import { MEDIA_LIBRARY_REAL_SHAPE, MEDIA_LIBRARY_DELETE, MEDIA_LIBRARY_RENAME } from "./featureFlag";
import { MEDIA_LIFECYCLE_SAFE_DELETE_ENABLED } from "../../constants/featureFlags";
import useMediaLibraryUpload from "./useMediaLibraryUpload";
import { MEDIA_LIBRARY_ACCEPT } from "./upload.logic";
import { getLoggedInUser } from "../../utils/helpers";

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "name", label: "Name" },
  { key: "size", label: "Size" },
];

const MEDIA_KEY = ["mediaLibrary", "all"];
const STORAGE_KEY = ["mediaLibrary", "storage"];

export default function MediaLibrary() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mediaQuery = useQuery(
    MEDIA_KEY,
    async () => {
      const res = await getTotalMedia();
      if (MEDIA_LIBRARY_REAL_SHAPE) {
        // EW-1280 fix: the LIVE shape is res.data.data.types = { category: { data: [
        // …real files… ] } }. Flatten each category's real array into flat items.
        const types = res?.data?.data?.types ?? {};
        return normalizeMediaTypes(types);
      }
      // Legacy (flag-OFF, byte-identical): the original wrong-depth unwrap +
      // normalizer, kept verbatim so OFF behaviour is unchanged.
      const grouped = res?.data?.data?.data?.data ?? res?.data?.data ?? {};
      return normalizeMediaResponse(grouped);
    },
    { staleTime: 60_000 }
  );

  const storageQuery = useQuery(
    STORAGE_KEY,
    async () => normalizeStorage((await getTotalStorage())?.data?.data ?? {}),
    { staleTime: 60_000 }
  );

  // ── UI state ────────────────────────────────────────────────────────────────
  const [activeType, setActiveType] = useState("all");
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState(SORTS[0].key);
  const [viewMode, setViewMode] = useState("large");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteItems, setDeleteItems] = useState(null);
  // ISP-9999 medialib-multiselect-partial-delete: step-2 followup, populated
  // only when a multi-select delete came back with 1+ locked files. The BE has
  // already deleted every other file in the batch by the time this is set.
  const [blockedItems, setBlockedItems] = useState(null);
  const [renameItem, setRenameItem] = useState(null);
  // #960: single-click PLAY / double-click EXPAND — { item, mode: "play"|"expand" }.
  const [preview, setPreview] = useState(null);
  // media-delete-modal-ux cluster: bumped after the delete modal's "Change
  // profile picture" flow saves a new avatar, so `profileImageUrl` below
  // (read from the localStorage snapshot getLoggedInUser() wraps, not React
  // state) gets re-read on the next render — the profile-picture delete guard
  // clears without a full page reload.
  const [profileImageVersion, setProfileImageVersion] = useState(0);
  const profileImageUrl = useMemo(() => {
    const u = getLoggedInUser();
    return u?.profile_image || u?.user_profile?.profile_image || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileImageVersion]);

  const allItems = mediaQuery.data || [];

  const filtered = useMemo(
    () => sortMedia(filterMedia(allItems, { activeType, query }), sortKey),
    [allItems, activeType, query, sortKey]
  );
  // Toolbar's "Sort: X" label only reflects the 4-state toolbar cycle
  // (Newest/Oldest/Name/Size); a list-view column-header sort (e.g. "type",
  // reachable only via the Type column) has no toolbar label and falls back
  // to the first entry rather than showing something misleading.
  const sortLabel = (SORTS.find((s) => s.key === sortKey) || SORTS[0]).label;
  const pageItems = useMemo(() => paginate(filtered, page, perPage), [filtered, page, perPage]);

  // Folders are owned by the Gallery/Folders subsystem; until wired we show none
  // (the empty-state path renders) — the view degrades honestly.
  const folders = mediaQuery.data?.folders || [];

  const status = storageQuery.isLoading || mediaQuery.isLoading
    ? "loading"
    : mediaQuery.isError
      ? "error"
      : allItems.length === 0
        ? "empty"
        : "ready";

  const resetPage = useCallback(() => setPage(1), []);
  const selectedItems = allItems.filter((i) => selectedIds.includes(i.id));

  // useCallback so the upload hook's `onUploaded` dep is stable across renders
  // (queryClient is stable) — otherwise its change handler is rebuilt every render.
  const invalidateMedia = useCallback(
    () => queryClient.invalidateQueries(MEDIA_KEY),
    [queryClient]
  );

  const invalidateStorage = useCallback(
    () => queryClient.invalidateQueries(STORAGE_KEY),
    [queryClient]
  );

  const invalidateAfterUpload = useCallback(() => {
    invalidateMedia();
    invalidateStorage();
  }, [invalidateMedia, invalidateStorage]);

  // ── ML-UP-01: a REAL upload, replacing the dead "Upload media" CTA ───────────
  // Was: `onUpload={() => mediaQuery.refetch()}` — the page's only call to action
  // silently re-ran the list query (measured live: no file chooser, no dialog, no
  // navigation; the whole page had ZERO input[type=file] and ZERO upload controls).
  // The chooser + validation + sequencing + reporting live in useMediaLibraryUpload
  // so this container stays readable; it uploads through the SAME presigned-S3
  // helper every other upload surface uses (utils/fileUpload#handleFileUpload,
  // moderation dispatch included), then invalidates the media + storage queries.
  const { fileInputRef, uploading, openFilePicker, onFilesChosen } =
    useMediaLibraryUpload(invalidateAfterUpload);

  // EW-1281: real deletion. The button was a client-only no-op — it re-invalidated
  // the query and the "deleted" item reappeared, despite "This can't be undone".
  // Flag-gated (MEDIA_LIBRARY_DELETE) because it flips a harmless no-op into an
  // irreversible backend DELETE. `deleteMediaFiles` posts the files' storage paths
  // to api/media/delete/ (the same endpoint + payload the Storage table already
  // uses). A 200 with `success:false` is treated as a failure so we never falsely
  // report a delete that the BE rejected.
  // Synchronous in-flight latch: react-query's isLoading only flips on the next
  // render, so it can't stop a same-tick second click (fast double-click / Enter
  // key-repeat). This ref does, and is released when the mutation settles.
  const deleteInFlight = useRef(false);

  const deleteMutation = useMutation(
    async (items) => {
      const paths = (items || []).map((i) => i.path).filter(Boolean);
      if (paths.length === 0) {
        throw new Error("These items can't be deleted (no storage path).");
      }
      let res;
      try {
        res = await deleteMediaFiles(paths);
      } catch (err) {
        // media-delete-guard-fe-modal: a non-2xx (e.g. the BE delete-guard's 409
        // for a still-referenced file) rejects with an AxiosError whose OWN
        // `.message` is the generic "Request failed with status code 409" — the
        // server's actual message is one level deeper, at `response.data.message`.
        throw new Error(err?.response?.data?.message || err?.message || "Delete failed. Please try again.");
      }
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Delete failed. Please try again.");
      }
      // ISP-9999 medialib-multiselect-partial-delete: for a 2+ file batch the BE
      // now reports a per-item outcome instead of 4xx-ing the whole request over
      // one locked file (`data.results`: [{path, deleted, reason}]) — everything
      // NOT blocked has already been deleted server-side. A single-item delete
      // keeps the legacy contract (throws above on any non-2xx/success:false), so
      // `results` is only ever present here for a batch of 2+.
      const results = res?.data?.data?.results;
      if (!Array.isArray(results)) return { blocked: [] };
      const blocked = results
        .filter((r) => !r.deleted)
        .map((r) => {
          const original = items.find((i) => i.path === r.path);
          return { ...original, path: r.path, reason: r.reason };
        });
      return { blocked };
    },
    {
      onSuccess: ({ blocked }) => {
        setDeleteItems(null);
        setSelectedIds([]);
        invalidateMedia();
        invalidateStorage();
        if (blocked.length > 0) setBlockedItems(blocked);
      },
      onSettled: () => { deleteInFlight.current = false; },
    }
  );

  // WL-072: the Wheels each item-in-the-delete-modal is attached to. Fetched
  // ONLY while the modal is open (never on the full grid — that would mean a
  // wheel-scan per paginated item, an unbounded cost the grid doesn't need),
  // scoped to exactly the files being considered for deletion. Inert
  // (`tracked:false`) unless MEDIA_LIFECYCLE_SAFE_DELETE_ENABLED, matching every
  // other WL-072 read below — flag-off stays byte-identical to the pre-WL-072
  // "Usage not tracked yet" state.
  const deletePaths = useMemo(
    () => (deleteItems || []).map((i) => i.path).filter(Boolean),
    [deleteItems]
  );
  const WHEELS_KEY = useMemo(() => ["mediaLibrary", "wheels", ...deletePaths], [deletePaths]);
  const wheelsQuery = useQuery(
    WHEELS_KEY,
    async () => {
      const res = await getMediaWheels(deletePaths);
      return res?.data?.data ?? { tracked: false, wheels: {} };
    },
    { enabled: MEDIA_LIFECYCLE_SAFE_DELETE_ENABLED && deletePaths.length > 0 }
  );

  // Merge the real refcount/wheel-list onto the items the modal renders. When
  // the BE hasn't shipped tracking yet (flag off, or the query hasn't resolved),
  // `tracked` stays false and each item keeps its adapter-supplied `usedInWheels`
  // (null → "Usage not tracked yet") — never fabricate a count.
  const itemsWithUsage = useMemo(() => {
    if (!deleteItems) return [];
    const tracked = Boolean(wheelsQuery.data?.tracked);
    const wheelsByPath = wheelsQuery.data?.wheels || {};
    return deleteItems.map((item) => {
      if (!tracked || !item.path) return item;
      const wheels = wheelsByPath[item.path] || [];
      return { ...item, usedInWheels: wheels.length, wheels };
    });
  }, [deleteItems, wheelsQuery.data]);

  const [selectedWheelIds, setSelectedWheelIds] = useState([]);

  // WL-072: "remove from selected Wheels" — the alternative to a permanent
  // Library delete. Detaches the ONE file (the modal's checklist only renders
  // for a single-item delete, see DeleteMediaModal) from the checked Wheels
  // and leaves the file itself, and every un-checked Wheel's reference, alone.
  const removeFromWheelsMutation = useMutation(
    async ({ path, wheelIds }) => {
      if (!path || !wheelIds || wheelIds.length === 0) {
        throw new Error("Select at least one Wheel to remove this file from.");
      }
      const res = await removeMediaFromWheels(path, wheelIds);
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Remove failed. Please try again.");
      }
      return res;
    },
    {
      onSuccess: () => {
        setDeleteItems(null);
        setSelectedIds([]);
        setSelectedWheelIds([]);
        invalidateMedia();
        invalidateStorage();
        queryClient.invalidateQueries(["mediaLibrary", "wheels"]);
      },
    }
  );

  // gordon-0831-media-library-rename: real rename wiring. `renameMedia` posts
  // the file's storage path + new name to api/media/rename/ (BE stores a
  // display-name override, the S3 key/URL never changes), then invalidates
  // the media query so the new name shows on the very next read.
  const renameMutation = useMutation(
    async ({ item, name }) => {
      if (!item?.path) {
        throw new Error("This item can't be renamed (no storage path).");
      }
      const res = await renameMedia(item.path, name);
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Rename failed. Please try again.");
      }
      return res;
    },
    {
      onSuccess: () => {
        setRenameItem(null);
        invalidateMedia();
      },
    }
  );

  const closeDeleteModal = () => {
    if (deleteMutation.isLoading || removeFromWheelsMutation.isLoading) return; // don't dismiss mid-action
    queryClient.cancelQueries(WHEELS_KEY);
    deleteMutation.reset();
    removeFromWheelsMutation.reset();
    setSelectedWheelIds([]);
    setDeleteItems(null);
  };

  const confirmDelete = (items) => {
    if (!MEDIA_LIBRARY_DELETE) {
      setDeleteItems(null);
      setSelectedIds([]);
      return;
    }
    if (deleteInFlight.current || deleteMutation.isLoading) return;
    deleteInFlight.current = true;
    deleteMutation.mutate(items);
  };

  return (
    <>
      {/* CM9 / E2E-RT-001: the Media Library rendered with NO app chrome at all —
          measured live, `document.querySelector("header")` was null and the page
          carried ZERO anchors, so there was no logo, no nav, no account menu and
          literally no in-app way off the page (browser Back only). Mount the same
          shared GalleryHeader its sibling media route /capture already mounts —
          reused as-is, not reimplemented, so the two media surfaces stay identical. */}
      <GalleryHeader />

      {/* ML-UP-01: the real upload entry point. `display:none` file input kept
          programmatically clickable via the hook's ref, and aria-hidden because the
          labelled Upload buttons in the view are the accessible controls. */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={MEDIA_LIBRARY_ACCEPT}
        onChange={onFilesChosen}
        aria-hidden="true"
        tabIndex={-1}
        style={{ display: "none" }}
        data-testid="media-library-file-input"
      />

      <MediaLibraryView
        status={status}
        uploading={uploading}
        storage={storageQuery.data || { usedLabel: "0 MB", totalLabel: "5 GB", percent: 0 }}
        onUpgrade={() => navigate("/settings?tab=subscriptions")}
        folders={folders}
        items={pageItems}
        total={filtered.length}
        activeType={activeType}
        query={query}
        sortLabel={sortLabel}
        sortKey={sortKey}
        viewMode={viewMode}
        page={page}
        perPage={perPage}
        selectedIds={selectedIds}
        onType={(t) => { setActiveType(t); resetPage(); }}
        onQuery={(q) => { setQuery(q); resetPage(); }}
        onClearFilters={() => { setQuery(""); setActiveType("all"); resetPage(); }}
        onSort={() => setSortKey((k) => {
          const idx = SORTS.findIndex((s) => s.key === k);
          return SORTS[(idx === -1 ? 0 : idx + 1) % SORTS.length].key;
        })}
        onColumnSort={(column) => setSortKey(COLUMN_SORT_KEYS[column] || "newest")}
        onViewMode={setViewMode}
        onPage={setPage}
        onPerPage={(n) => { setPerPage(n); resetPage(); }}
        onUpload={openFilePicker}
        onPlayItem={(item) => setPreview({ item, mode: "play" })}
        onExpandItem={(item) => setPreview({ item, mode: "expand" })}
        onToggleSelectItem={(item) => {
          setSelectedIds((cur) =>
            cur.includes(item.id) ? cur.filter((x) => x !== item.id) : [...cur, item.id]
          );
        }}
        onRetry={() => mediaQuery.refetch()}
        onBulkDelete={() => { setSelectedWheelIds([]); setDeleteItems(selectedItems); }}
        onClearSelection={() => setSelectedIds([])}
        onRenameItem={MEDIA_LIBRARY_RENAME ? (item) => setRenameItem(item) : undefined}
        onSelectAll={(selectAll) => setSelectedIds(selectAll ? pageItems.map((i) => i.id) : [])}
      />

      <DeleteMediaModal
        open={Boolean(deleteItems)}
        items={itemsWithUsage}
        busy={deleteMutation.isLoading}
        error={deleteMutation.isError ? (deleteMutation.error?.message || "Delete failed. Please try again.") : null}
        profileImageUrl={profileImageUrl}
        onCancel={closeDeleteModal}
        onConfirm={confirmDelete}
        onProfilePictureChanged={() => setProfileImageVersion((v) => v + 1)}
        wheelsLoading={MEDIA_LIFECYCLE_SAFE_DELETE_ENABLED && wheelsQuery.isLoading}
        selectedWheelIds={selectedWheelIds}
        onToggleWheel={(wheelId) =>
          setSelectedWheelIds((cur) =>
            cur.includes(wheelId) ? cur.filter((id) => id !== wheelId) : [...cur, wheelId]
          )
        }
        removeBusy={removeFromWheelsMutation.isLoading}
        removeError={
          removeFromWheelsMutation.isError
            ? (removeFromWheelsMutation.error?.message || "Remove failed. Please try again.")
            : null
        }
        onRemoveFromWheels={(item) =>
          removeFromWheelsMutation.mutate({ path: item.path, wheelIds: selectedWheelIds })
        }
      />

      <DeleteMediaBlockedModal
        open={Boolean(blockedItems)}
        items={blockedItems || []}
        onClose={() => setBlockedItems(null)}
      />

      {MEDIA_LIBRARY_RENAME && (
        <RenamePaletteDialog
          open={Boolean(renameItem)}
          onClose={() => { renameMutation.reset(); setRenameItem(null); }}
          onConfirm={(name) => renameMutation.mutate({ item: renameItem, name })}
          initialValue={renameItem?.name || ""}
          title="Rename file"
          placeholder="File name"
          message={
            renameMutation.isError
              ? renameMutation.error?.message || "Rename failed. Please try again."
              : "Enter a new name for this file"
          }
        />
      )}

      {preview && (
        <MediaPreviewModal
          item={preview.item}
          mode={preview.mode}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}
